import { computed } from "vue";
import { useAwardStore } from "@/stores";
import useSWRV from "swrv";
import { getDefaultAwardTable } from "@/stores/award";
import { isPast, isValid, parseISO } from "date-fns";

export default function useAwards() {
  const awardStore = useAwardStore();

  const { data: awards, mutate: refreshAwards } = useSWRV(
    "/awards",
    () => awardStore.getAwards(),
    { refreshInterval: 60000 }
  );

  const activeAward = computed(() => {
    const tableName = getDefaultAwardTable();
    return awards.value?.find((award) => award.table === tableName);
  });

  const instances = computed(() => {
    return (
      activeAward.value?.instances.filter((instance) => instance.awardYear) ??
      []
    );
  });

  const instance = computed(() => {
    if (!instances.value.length) {
      return null;
    }
    const latestInstance = instances.value[0];
    if (latestInstance.isDraft && instances.value.length > 1) {
      return instances.value[1];
    }
    return instances.value[0];
  });

  const settings = computed(() => {
    return activeAward.value?.settings ?? null;
  });

  const awardYear = computed<number>(() => {
    return instances.value[0]?.awardYear || settings.value?.awardYear || 0;
  });

  const awardYears = computed(() => {
    const years = instances.value.flatMap((instance) => instance.awardYear);
    return [...new Set(years.filter((year) => !!year))] as Array<number>;
  });

  const regions = computed(() => {
    return instance.value?.regions ?? null;
  });

  const nominationStartDate = computed(() => {
    if (!instance.value?.nominationStartDate) {
      return null;
    }
    const date = parseISO(instance.value.nominationStartDate);
    return isValid(date) ? date : null;
  });

  const nominationEndDate = computed(() => {
    if (!instance.value?.nominationEndDate) {
      return null;
    }
    const date = parseISO(instance.value.nominationEndDate);
    return isValid(date) ? date : null;
  });

  const votingStartDate = computed(() => {
    if (!instance.value?.votingStartDate) {
      return null;
    }
    const date = parseISO(instance.value.votingStartDate);
    return isValid(date) ? date : null;
  });

  const votingEndDate = computed(() => {
    if (!instance.value?.votingEndDate) {
      return null;
    }
    const date = parseISO(instance.value.votingEndDate);
    return isValid(date) ? date : null;
  });

  const awardsCeremonyOver = computed(() => {
    const { awardsCeremonyNights = [] } = instance.value ?? {};
    const night = awardsCeremonyNights[awardsCeremonyNights.length - 1];
    if (!night) {
      return false;
    }
    return isPast(parseISO(night.date));
  });

  const afterCutoff = computed(() => {
    if (!instance.value?.cutoff) {
      return false;
    }
    const cutoff = parseISO(instance.value.cutoff);
    return isPast(cutoff);
  });

  return {
    activeAward,
    awards,
    awardsCeremonyOver,
    awardYear,
    awardYears,
    instance,
    instances,
    nominationStartDate,
    nominationEndDate,
    regions,
    settings,
    afterCutoff,
    refresh: () => Promise.all([refreshAwards()]),
    votingStartDate,
    votingEndDate,
  };
}
